import { useMemo, useState } from 'react';
import { useStore } from '@/context/StoreContext';
import { navigate } from '@/hooks/useRoute';
import type { Product } from '@/types/product';

const money=(n:number)=>new Intl.NumberFormat('he-IL',{style:'currency',currency:'ILS',maximumFractionDigits:0}).format(n);
const inCategory=(p:Product,key:string)=>p.category===key||(p.categoryKeys||[]).includes(key);
const publicSku=(p:Product)=>String((p as any).mielleySku||p.sku||'');
const supplierSku=(p:Product)=>String((p as any).supplierSku||(p as any).admin?.supplierSku||((p as any).mielleySku?p.sku:'')||'');
const supplierName=(p:Product)=>String((p as any).admin?.supplier||(p as any).source||'ידני');
const privateCost=(p:Product)=>Number((p as any).admin?.supplierGross??p.cost??0)||0;
const statusHe=(s:Product['status'])=>s==='active'?'פעיל':s==='draft'?'טיוטה':'מוסתר';

export function AdminProducts(){
 const {products,deleteProduct,saveProduct,cms}=useStore();
 const globalQ=sessionStorage.getItem('mielley_admin_global_search')||'';
 const [q,setQ]=useState(globalQ);
 const [category,setCategory]=useState('');
 const [status,setStatus]=useState('');
 const [stock,setStock]=useState('');
 const [supplier,setSupplier]=useState('');
 const [selected,setSelected]=useState<number[]>([]);
 if(globalQ) sessionStorage.removeItem('mielley_admin_global_search');

 const supplierOptions=useMemo(()=>[...new Set(products.map(supplierName).filter(Boolean))].sort(),[products]);
 const rows=useMemo(()=>products.filter(p=>{
   const hay=[p.name,publicSku(p),supplierSku(p),p.categoryLabel,p.subcategory,supplierName(p),...(p.sourceCategories||[])].join(' ').toLowerCase();
   return (!q||hay.includes(q.toLowerCase()))
    &&(!category||inCategory(p,category))
    &&(!status||p.status===status)
    &&(!supplier||supplierName(p)===supplier)
    &&(!stock||(stock==='low'?p.stockQty<=p.lowStockThreshold:stock==='ready'?p.readyToShip:p.stockQty>0));
 }),[products,q,category,status,stock,supplier]);

 const stats=useMemo(()=>({
   active:products.filter(p=>p.status==='active').length,
   draft:products.filter(p=>p.status==='draft').length,
   low:products.filter(p=>p.stockQty<=p.lowStockThreshold).length,
   sale:products.filter(p=>Number(p.oldPrice||0)>Number(p.price||0)).length
 }),[products]);

 const toggle=(id:number)=>setSelected(x=>x.includes(id)?x.filter(v=>v!==id):[...x,id]);
 const toggleVisibility=async(p:Product)=>{
   const nextStatus:Product['status']=p.status==='hidden'?'active':'hidden';
   if(!confirm(`${nextStatus==='hidden'?'להסתיר':'להציג מחדש'} את "${p.name}"?`))return;
   await saveProduct({...p,status:nextStatus});
 };
 const bulk=async(action:'active'|'draft'|'hidden'|'featured'|'ready')=>{
   const items=products.filter(p=>selected.includes(p.id));
   for(const p of items){
     if(action==='featured') await saveProduct({...p,featured:true});
     else if(action==='ready') await saveProduct({...p,readyToShip:true});
     else await saveProduct({...p,status:action});
   }
   setSelected([]);
 };
 const exportCsv=()=>{
   const esc=(x:unknown)=>`"${String(x??'').replace(/"/g,'""')}"`;
   const header=['id','mielleySku','supplierSku','supplier','name','category','regularPrice','salePrice','cost','stock','status'];
   const lines=[header.join(','),...rows.map(p=>[
     p.id,publicSku(p),supplierSku(p),supplierName(p),p.name,p.category,p.oldPrice,p.price,privateCost(p),p.stockQty,p.status
   ].map(esc).join(','))];
   const blob=new Blob(['\ufeff'+lines.join('\n')],{type:'text/csv;charset=utf-8'});
   const url=URL.createObjectURL(blob);const a=document.createElement('a');
   a.href=url;a.download='mielley-products.csv';a.click();URL.revokeObjectURL(url);
 };

 return <div className="admin-content mav2-products-page">
   <div className="mav2-products-title">
    <div><span>CATALOG</span><h2>ניהול מוצרים</h2><p>קטלוג מסודר עם MIELLEY SKU, ספק, מחירים, מלאי וסטטוס.</p></div>
    <div><button className="mav2-btn light" onClick={exportCsv}>⇩ ייצוא</button><button className="mav2-btn primary" onClick={()=>navigate('/admin/products/new')}>＋ הוספת מוצר חדש</button></div>
   </div>

   <div className="mav2-product-mini-stats">
    <article><span>סה״כ</span><b>{products.length}</b></article>
    <article><span>פעילים</span><b>{stats.active}</b></article>
    <article><span>טיוטות</span><b>{stats.draft}</b></article>
    <article className={stats.low?'warn':''}><span>מלאי נמוך</span><b>{stats.low}</b></article>
    <article><span>במבצע</span><b>{stats.sale}</b></article>
   </div>

   <section className="mav2-panel mav2-products-card">
    <div className="mav2-products-toolbar">
      <div className="mav2-search-box"><span>⌕</span><input value={q} onChange={e=>setQ(e.target.value)} placeholder="חיפוש לפי שם מוצר, MIELLEY SKU או מק״ט ספק..." /></div>
      <select value={category} onChange={e=>setCategory(e.target.value)}><option value="">כל הקטגוריות</option>{Object.entries(cms.categories).map(([k,c])=><option key={k} value={k}>{c.label}</option>)}</select>
      <select value={supplier} onChange={e=>setSupplier(e.target.value)}><option value="">כל המקורות</option>{supplierOptions.map(x=><option key={x} value={x}>{x}</option>)}</select>
      <select value={status} onChange={e=>setStatus(e.target.value)}><option value="">כל הסטטוסים</option><option value="active">פעיל</option><option value="draft">טיוטה</option><option value="hidden">מוסתר</option></select>
      <select value={stock} onChange={e=>setStock(e.target.value)}><option value="">כל המלאי</option><option value="in">במלאי</option><option value="low">מלאי נמוך</option><option value="ready">מוכנים למשלוח</option></select>
      <span className="mav2-result-count">{rows.length} מוצרים</span>
    </div>

    {selected.length>0&&<div className="mav2-bulk">
      <b>{selected.length} נבחרו</b><button onClick={()=>void bulk('active')}>הפעל</button><button onClick={()=>void bulk('draft')}>טיוטה</button><button onClick={()=>void bulk('hidden')}>הסתר</button><button onClick={()=>void bulk('featured')}>מומלץ</button><button onClick={()=>void bulk('ready')}>מוכן למשלוח</button><button onClick={()=>setSelected([])}>ביטול</button>
    </div>}

    <div className="mav2-products-table">
      <div className="mav2-products-row head">
        <span><input type="checkbox" checked={rows.length>0&&rows.every(p=>selected.includes(p.id))} onChange={e=>setSelected(e.target.checked?rows.map(p=>p.id):[])}/></span>
        <span>מוצר</span><span>MIELLEY SKU</span><span>מק״ט ספק <small>פנימי בלבד</small></span><span>קטגוריה</span><span>מלאי</span><span>מחיר רגיל</span><span>מחיר מכירה</span><span>סטטוס</span><span>פעולות</span>
      </div>
      {rows.map(p=>{
        const regular=Number(p.oldPrice||0);
        const sale=Number(p.price||0);
        return <div className="mav2-products-row" key={p.id}>
          <span><input type="checkbox" checked={selected.includes(p.id)} onChange={()=>toggle(p.id)}/></span>
          <span className="mav2-product-cell"><img src={p.image} alt=""/><span><b>{p.name}</b><small>{supplierName(p)}{p.featured?' · מומלץ':''}{p.readyToShip?' · מוכן למשלוח':''}</small></span></span>
          <span className="mav2-public-sku"><b>{publicSku(p)||'—'}</b><button title="העתק MIELLEY SKU" onClick={()=>void navigator.clipboard?.writeText(publicSku(p))}>□</button></span>
          <span className="mav2-private-sku"><b>{supplierSku(p)||'—'}</b><small>{supplierName(p)}</small></span>
          <span><b>{cms.categories[p.category]?.label||p.categoryLabel}</b><small>{p.subcategory||''}</small></span>
          <span className={p.stockQty<=p.lowStockThreshold?'mav2-stock low':'mav2-stock'}><b>{p.stockQty}</b><small>{p.stockQty<=p.lowStockThreshold?'מלאי נמוך':'במלאי'}</small></span>
          <span className="mav2-regular-price">{regular>0?money(regular):'—'}</span>
          <span className="mav2-sale-price"><b>{money(sale)}</b>{regular>sale&&sale>0?<small>מבצע</small>:null}</span>
          <span><i className={`mav2-status ${p.status}`}>{statusHe(p.status)}</i></span>
          <span className="mav2-row-actions">
            <button title="עריכה" onClick={()=>navigate(`/admin/products/${p.id}`)}>✎</button>
            <button title="צפייה באתר" onClick={()=>navigate(`/product/${p.slug}`)}>▣</button>
            <button title={p.status==='hidden'?'הצג':'הסתר'} onClick={()=>void toggleVisibility(p)}>◇</button>
            <button className="danger" title="מחיקה" onClick={()=>{if(confirm('למחוק את המוצר?'))void deleteProduct(p.id)}}>⋮</button>
          </span>
        </div>
      })}
      {!rows.length&&<div className="mav2-empty products">לא נמצאו מוצרים לפי הסינון שבחרת.</div>}
    </div>
   </section>
 </div>
}
